import {
  collection,
  onSnapshot,
  orderBy,
  query,
  limit as fsLimit,
  Unsubscribe
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { apiClient } from './apiClient';
import { Contact } from '../../types/models/whatsapp';

const CONTACTS = 'whatsapp_contacts';
const DEFAULT_PAGE_SIZE = 200;

export interface ImportRow {
  phone: string;
  name?: string;
}

export interface ImportResult {
  created: number;
  skipped: number;
  invalid: Array<{ row: number; phone: string; reason: string }>;
}

/**
 * Contacts: live reads from Firestore, writes through the API so phone
 * normalization and duplicate detection stay server-side.
 */
class ContactService {
  private map(id: string, data: any): Contact {
    return {
      id,
      phone: data.phone || '',
      name: data.name || '',
      attributes: data.attributes || {},
      tags: Array.isArray(data.tags) ? data.tags : [],
      optIn: data.optIn || { status: 'unknown', source: '', updatedAt: 0 },
      blocked: data.blocked === true,
      blockedAt: data.blockedAt ?? null,
      blockedBy: data.blockedBy ?? null,
      linkedUserId: data.linkedUserId,
      lastSeenAt: data.lastSeenAt ?? null,
      createdAt: data.createdAt || 0,
      updatedAt: data.updatedAt || 0
    };
  }

  subscribe(
    onChange: (contacts: Contact[]) => void,
    onError?: (error: Error) => void,
    pageSize: number = DEFAULT_PAGE_SIZE
  ): Unsubscribe {
    if (!db) {
      onError?.(new Error('Firestore unavailable'));
      return () => {};
    }

    const q = query(
      collection(db, CONTACTS),
      orderBy('createdAt', 'desc'),
      fsLimit(pageSize)
    );

    return onSnapshot(
      q,
      (snapshot) => onChange(snapshot.docs.map((d) => this.map(d.id, d.data()))),
      (error) => {
        console.error('❌ Contact subscription error:', error);
        onError?.(error as Error);
      }
    );
  }

  async create(input: {
    phone: string;
    name?: string;
    tags?: string[];
  }): Promise<{ id: string; phone: string; existed: boolean }> {
    return apiClient.post('/contacts', input);
  }

  async importRows(rows: ImportRow[]): Promise<ImportResult> {
    return apiClient.post<ImportResult>('/contacts/import', { rows });
  }

  /** Opens or reuses a conversation, creating the contact when new. */
  async startConversation(
    phone: string,
    name?: string
  ): Promise<{ conversationId: string; contactId: string; existed: boolean }> {
    return apiClient.post('/conversations', { phone, name });
  }

  async setBlocked(contactId: string, blocked: boolean): Promise<void> {
    await apiClient.patch(`/contacts/${contactId}`, { blocked });
  }

  async setOptIn(
    contactId: string,
    optInStatus: 'opted_in' | 'opted_out' | 'unknown'
  ): Promise<void> {
    await apiClient.patch(`/contacts/${contactId}`, { optInStatus });
  }

  async rename(contactId: string, name: string): Promise<void> {
    await apiClient.patch(`/contacts/${contactId}`, { name });
  }

  /**
   * Edits the fields an agent owns. Sends only what changed, so a concurrent
   * webhook update to lastSeenAt is not clobbered.
   */
  async update(
    contactId: string,
    changes: {
      name?: string;
      tags?: string[];
      attributes?: Record<string, string | number | boolean>;
      optInStatus?: 'opted_in' | 'opted_out' | 'unknown';
    }
  ): Promise<void> {
    await apiClient.patch(`/contacts/${contactId}`, changes);
  }

  /**
   * Records consent for several contacts at once, for consent collected
   * outside this app. Contacts who opted out are never touched — only they
   * can reverse that.
   */
  async bulkOptIn(
    contactIds: string[]
  ): Promise<{
    updated: number;
    skipped: { id: string; name?: string; reason: string }[];
  }> {
    return apiClient.post('/contacts/bulk-opt-in', { ids: contactIds });
  }

  /**
   * Deletes several contacts at once.
   *
   * Partial success is normal — a contact queued in a running campaign is
   * refused individually — so the result lists what was skipped and why
   * rather than failing the whole batch.
   */
  async removeMany(
    contactIds: string[]
  ): Promise<{
    deleted: number;
    skipped: { id: string; name?: string; reason: string }[];
    detachedConversations: number;
  }> {
    return apiClient.post('/contacts/bulk-delete', { ids: contactIds });
  }

  /**
   * Deletes the contact. Conversations survive as a detached record of what
   * was said; the response reports how many were detached.
   */
  async remove(
    contactId: string
  ): Promise<{ success: boolean; detachedConversations: number }> {
    return apiClient.delete(`/contacts/${contactId}`);
  }
}

export const contactService = new ContactService();
export default contactService;
